import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CriminalCoverNoteModal } from '../../components/coverNote/CriminalCoverNoteModal';
import CommonCoverNoteModal from '../../components/wizard/CommonCoverNoteModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { PhysicalFileRetrievalModal } from '../../components/tasks/PhysicalFileRetrievalModal';
import { DataTable, type Column } from '../../components/table/DataTable';
import type { TaskCaseRow } from '../../data/tasks';
import {
  TASK_EMPTY_DISPLAY,
  taskStatusPillClass,
  timeRemainingClass,
  urgencyTextClass,
  type TaskListScope,
} from '../../data/taskStatus';
import {
  deleteTaskDraft,
  duplicateTaskCase,
  fetchTaskColumnSuggestions,
  fetchTaskTabCounts,
  listTasks,
  requestPhysicalFileRetrieval,
  transferTaskCase,
} from '../../services/tasks';
import { useContinueDraftCase } from '../../hooks/useContinueDraftCase';
import { isCaseRequestUuid } from '../task-case/taskCaseUpdateHelpers';
import CriminalAppellateCoverNoteModal from '../wizard/criminalAppellate/CriminalAppellateCoverNoteModal';
import RightToInformationCoverNoteModal from '../wizard/rightToInformation/RightToInformationCoverNoteModal';
import { setFlashToast } from '../../utils/flashToast';
import { useTaskCaseDetailsNavigation } from '../../hooks/useTaskCaseDetailsNavigation';
import { TASKS_CASES_RETURN } from '../task-case/taskCaseDetailsReturnNavigation';
import { criminalWizardPath } from '../wizard/criminal/CriminalWizardShell';
import {
  usesCommonCoverNote,
  usesCriminalAppellateCoverNote,
  wizardFlowForCategoryName,
} from '../../utils/wizardCategoryRoute';
import { taskCaseRowHighlightClass } from '../../utils/taskRowHighlight';

const ANIM_FRAMES = ['.', '..', '...'] as const;
const EMPTY = TASK_EMPTY_DISPLAY;

type MainTab = 'active' | 'completed';
type SubTab = 'mine' | 'linked' | 'created';

function listScope(mainTab: MainTab, subTab: SubTab): TaskListScope {
  if (mainTab === 'completed') return 'completed';
  if (subTab === 'mine') return 'active-mine';
  if (subTab === 'created') return 'active-created';
  return 'active-linked';
}

type TaskTabCounts = { activeMine: number; activeLinked: number; activeCreated: number; completed: number };

function tabCountKey(scope: TaskListScope): keyof TaskTabCounts {
  if (scope === 'active-mine') return 'activeMine';
  if (scope === 'active-linked') return 'activeLinked';
  if (scope === 'active-created') return 'activeCreated';
  return 'completed';
}

function taskRowKey(r: TaskCaseRow): string {
  const caseId = r.caseRequestId ?? r.id;
  const taskId = r.userTaskId?.trim();
  return taskId ? `${caseId}:${taskId}` : caseId;
}

function isCriminalAdviceCategoryLabel(label: string): boolean {
  const normalized = label.trim().toLowerCase();
  return normalized.includes('criminal') && (normalized.includes('advice') || normalized.includes('advise'));
}

export default function TasksCasesPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { canUpdate, taskCaseLink } = useTaskCaseDetailsNavigation({ returnState: TASKS_CASES_RETURN });

  const [mainTab, setMainTab] = useState<MainTab>(() => {
    const v = (searchParams.get('mainTab') ?? '').trim().toLowerCase();
    return v === 'completed' ? 'completed' : 'active';
  });
  const [subTab, setSubTab] = useState<SubTab>(() => {
    const v = (searchParams.get('subTab') ?? '').trim().toLowerCase();
    if (v === 'linked') return 'linked';
    if (v === 'created') return 'created';
    return 'mine';
  });

  const [rows, setRows] = useState<TaskCaseRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tabCounts, setTabCounts] = useState<TaskTabCounts>({
    activeMine: 0,
    activeLinked: 0,
    activeCreated: 0,
    completed: 0,
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'ref', dir: 'asc' });

  const [coverRow, setCoverRow] = useState<TaskCaseRow | null>(null);
  const [retrievalRow, setRetrievalRow] = useState<TaskCaseRow | null>(null);
  const [retrievalSubmitting, setRetrievalSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<TaskCaseRow | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [transferId, setTransferId] = useState<string | null>(null);
  const { continueDraftId, handleContinueDraft } = useContinueDraftCase('/tasks');

  const [animFrame, setAnimFrame] = useState(0);
  const anyActionLoading =
    duplicateId !== null || transferId !== null || continueDraftId !== null || deleteSubmitting || retrievalSubmitting;

  useEffect(() => {
    if (!anyActionLoading) return;
    const id = setInterval(() => setAnimFrame((f) => (f + 1) % ANIM_FRAMES.length), 400);
    return () => clearInterval(id);
  }, [anyActionLoading]);

  const dots = ANIM_FRAMES[animFrame];

  const fetchAbortRef = useRef<AbortController | null>(null);
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersRef = useRef<Record<string, string>>({});
  const scope = listScope(mainTab, subTab);

  const fetchList = useCallback(
    (p: number, size: number, filters: Record<string, string>, sortKey: string, sortDir: 'asc' | 'desc', listScope: TaskListScope) => {
      fetchAbortRef.current?.abort();
      fetchAbortRef.current = new AbortController();
      const { signal } = fetchAbortRef.current;
      setLoading(true);
      setError(null);

      void listTasks({
        scope: listScope,
        page: p - 1,
        pageSize: size,
        columnFilters: filters,
        sortBy: sortKey,
        sortDir: sortDir === 'asc' ? 'ASC' : 'DESC',
        signal,
      })
        .then((result) => {
          if (signal.aborted) return;
          setRows(result.items);
          setTotalCount(result.total);
          setTotalPages(result.totalPages);
          setTabCounts((prev) => ({ ...prev, [tabCountKey(listScope)]: result.total }));
        })
        .catch((err: unknown) => {
          if (signal.aborted || (err instanceof Error && err.name === 'AbortError')) return;
          setError(t('tasks.cases.loadFailed'));
          setRows([]);
          setTotalCount(0);
          setTotalPages(1);
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [t],
  );

  const fetchRef = useRef(fetchList);
  useEffect(() => {
    fetchRef.current = fetchList;
  }, [fetchList]);

  const tabCountsAbortRef = useRef<AbortController | null>(null);

  const refreshTabCounts = useCallback(() => {
    tabCountsAbortRef.current?.abort();
    const ac = new AbortController();
    tabCountsAbortRef.current = ac;
    void fetchTaskTabCounts(ac.signal)
      .then((counts) => {
        if (!ac.signal.aborted) setTabCounts(counts);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshTabCounts();
    return () => tabCountsAbortRef.current?.abort();
  }, [refreshTabCounts]);

  const resetListState = useCallback(() => {
    setPage(1);
    filtersRef.current = {};
    setSort({ key: 'ref', dir: 'asc' });
    refreshTabCounts();
  }, [refreshTabCounts]);

  const selectMainTab = useCallback(
    (next: MainTab) => {
      setMainTab(next);
      resetListState();
    },
    [resetListState],
  );

  const selectSubTab = useCallback(
    (next: SubTab) => {
      setSubTab(next);
      resetListState();
    },
    [resetListState],
  );

  useEffect(() => {
    fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir, scope);
    return () => fetchAbortRef.current?.abort();
  }, [page, pageSize, sort.key, sort.dir, scope]);

  const reloadList = useCallback(() => {
    fetchRef.current(page, pageSize, filtersRef.current, sort.key, sort.dir, scope);
    refreshTabCounts();
  }, [page, pageSize, sort.key, sort.dir, scope, refreshTabCounts]);

  const handleFiltersChange = useCallback(
    (colFilters: Record<string, string>) => {
      filtersRef.current = colFilters;
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = setTimeout(() => {
        setPage(1);
        fetchRef.current(1, pageSize, colFilters, sort.key, sort.dir, scope);
      }, 400);
    },
    [pageSize, sort.key, sort.dir, scope],
  );

  const handleSortChange = useCallback((key: string, dir: 'asc' | 'desc') => {
    setSort({ key, dir });
    setPage(1);
  }, []);

  const handleDuplicate = useCallback(
    (row: TaskCaseRow) => {
      const id = row.caseRequestId ?? row.id;
      if (duplicateId) return;
      setDuplicateId(id);
      void duplicateTaskCase(id)
        .then((newId) => {
          if (!newId) {
            setFlashToast({ message: t('tasks.cases.duplicateFailed'), variant: 'error' });
            return;
          }
          setFlashToast({ message: t('tasks.cases.duplicateSuccess'), variant: 'success' });
          handleContinueDraft({
            id: newId,
            ref: newId,
            fileCategory: row.fileCategory !== EMPTY ? row.fileCategory : undefined,
          });
        })
        .finally(() => setDuplicateId(null));
    },
    [duplicateId, handleContinueDraft, t],
  );

  const handleTransfer = useCallback(
    (row: TaskCaseRow) => {
      const id = row.caseRequestId ?? row.id;
      if (transferId) return;
      setTransferId(id);
      void transferTaskCase(id)
        .then((newId) => {
          if (!newId) {
            setFlashToast({ message: t('tasks.cases.transferFailed'), variant: 'error' });
            return;
          }
          setFlashToast({ message: t('tasks.cases.transferSuccess'), variant: 'success' });
          nav(criminalWizardPath(1, newId), { state: { returnTo: '/tasks', resumeFlow: 'criminal' } });
        })
        .finally(() => setTransferId(null));
    },
    [transferId, t, nav],
  );

  const runDeleteDraft = () => {
    if (!confirmDelete || deleteSubmitting) return;
    const id = confirmDelete.caseRequestId ?? confirmDelete.id;
    setDeleteSubmitting(true);
    void deleteTaskDraft(id)
      .then((ok) => {
        if (!ok) {
          setFlashToast({ message: 'Failed to delete draft.', variant: 'error' });
          return;
        }
        setFlashToast({ message: 'Draft deleted successfully.', variant: 'success' });
        setConfirmDelete(null);
        reloadList();
      })
      .finally(() => setDeleteSubmitting(false));
  };

  const runRetrieval = () => {
    if (!retrievalRow || retrievalSubmitting) return;
    const id = retrievalRow.caseRequestId ?? retrievalRow.id;
    setRetrievalSubmitting(true);
    void requestPhysicalFileRetrieval(id)
      .then((r) => {
        if (!r.success) {
          setFlashToast({ message: r.message ?? 'Failed to request physical file retrieval.', variant: 'error' });
          return;
        }
        setFlashToast({
          message: 'Physical file retrieval initiated. Record Room has been notified.',
          variant: 'success',
        });
        setRetrievalRow(null);
        reloadList();
      })
      .finally(() => setRetrievalSubmitting(false));
  };

  const isDraft = (r: TaskCaseRow) => {
    // Some APIs provide task_status as "Draft"/"draft" while statusKey may not be normalized.
    const k = (r.statusKey ?? '').trim().toLowerCase();
    if (k === 'draft') return true;
    const s = (r.status ?? '').trim().toLowerCase();
    return s === 'draft';
  };
  const isRejected = (r: TaskCaseRow) => r.statusKey === 'rejected';

  const handleOpenCoverNote = useCallback(
    (row: TaskCaseRow) => {
      const caseRequestId = row.caseRequestId ?? row.id;
      if (!isCaseRequestUuid(caseRequestId)) {
        setFlashToast({ message: t('caseRequests.coverNoteSubmittedOnly'), variant: 'error' });
        return;
      }
      setCoverRow(row);
    },
    [t],
  );

  const coverFlow = coverRow ? wizardFlowForCategoryName(coverRow.fileCategory) : 'unknown';
  const coverCaseRequestId = coverRow ? (coverRow.caseRequestId ?? coverRow.id) : '';
  const coverFileRef = coverRow?.ref;

  const columns = useMemo<Column<TaskCaseRow>[]>(() => {
    const base: Column<TaskCaseRow>[] = [
      {
        key: 'ref',
        header: t('tasks.shared.fileRefNo'),
        getValue: (r) => r.ref,
        filterPattern: /^[a-zA-Z0-9/._ -]*$/,
        render: (r) => (
          <span className="rn">
            {isDraft(r) ? (
              r.ref
            ) : (
              <Link
                {...taskCaseLink({
                  caseRequestId: r.caseRequestId ?? r.id,
                  userTaskId: r.userTaskId,
                  viewMode: !canUpdate,
                })}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {r.ref}
              </Link>
            )}
          </span>
        ),
      },
      { key: 'fileCategory', header: t('tasks.shared.fileCategory'), getValue: (r) => r.fileCategory },
      {
        key: 'subFileCategory',
        header: t('tasks.shared.subFileCategory'),
        getValue: (r) => r.subFileCategory,
        render: (r) => <span style={{ color: r.subFileCategory === EMPTY ? 'var(--t3)' : undefined }}>{r.subFileCategory}</span>,
      },
      {
        key: 'agency',
        header: t('tasks.shared.submittedAgency'),
        getValue: (r) => r.agency,
        render: (r) => (
          <span style={{ color: r.agency === EMPTY ? 'var(--t3)' : undefined }}>{r.agency}</span>
        ),
      },
    ];

    if (mainTab === 'active') {
      base.push({
        key: 'submittedAt',
        header: t('tasks.shared.submittedAt'),
        filterType: 'date',
        getValue: (r) => r.submittedAtIso,
        render: (r) => (
          <span style={{ color: r.submittedAt === EMPTY ? 'var(--t3)' : undefined }}>{r.submittedAt}</span>
        ),
      });
      base.push({
        key: 'stage',
        header: t('tasks.shared.currentWorkflowStage'),
        getValue: (r) => r.stage,
        render: (r) =>
          r.stage === EMPTY ? (
            <span className="t-dash">{EMPTY}</span>
          ) : (
            <span className="pill p-aw">{r.stage}</span>
          ),
      });
    }

    base.push({
      key: 'status',
      header: t('tasks.shared.taskStatus'),
      getValue: (r) => r.status,
      render: (r) =>
        r.status === EMPTY ? (
          <span className="t-dash">{EMPTY}</span>
        ) : (
          <span className={`pill ${taskStatusPillClass(r.statusKey)}`}>{r.status}</span>
        ),
    });

    if (mainTab === 'active') {
      base.push({
        key: 'time',
        header: t('tasks.shared.timeRemaining'),
        getValue: (r) => r.timeLabel,
        sortable: true,
        render: (r) => (
          <span className={timeRemainingClass(r.isOverdue, r.hasTimeValue)}>{r.timeLabel}</span>
        ),
      });
    }

    base.push({
      key: 'urgency',
      header: t('tasks.shared.urgencyLevel'),
      getValue: (r) => r.urgencyLevel,
      render: (r) => <span className={urgencyTextClass(r.urgencyLevel)}>{r.urgencyLevel}</span>,
    });

    if (mainTab === 'active' && (subTab === 'linked' || subTab === 'created')) {
      base.push({
        key: 'officer',
        header: t('tasks.shared.assignedOfficer'),
        getValue: (r) => r.assignedOfficer ?? '',
        render: (r) => (
          <span style={{ color: r.assignedOfficer === EMPTY ? 'var(--t3)' : undefined }}>
            {r.assignedOfficer}
          </span>
        ),
      });
    }

    base.push({
      key: 'actions',
      header: t('common.actions'),
      getValue: () => '',
      searchable: false,
      sortable: false,
      render: (r) => {
        const rowId = r.caseRequestId ?? r.id;
        const isDuplicating = duplicateId === rowId;
        const isTransferring = transferId === rowId;
        const isContinuing = continueDraftId === rowId;
        const draft = isDraft(r);
        const canTransfer = !draft && isCriminalAdviceCategoryLabel(r.fileCategory);

        const duplicateBtn = !draft ? (
          <button
            className="act-b"
            type="button"
            data-tip={isDuplicating ? `${t('tasks.shared.duplicating')}${dots}` : t('tasks.shared.duplicateCase')}
            disabled={isDuplicating || duplicateId !== null}
            onClick={() => handleDuplicate(r)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
        ) : null;

        const transferBtn = canTransfer ? (
          <button
            className="act-b"
            type="button"
            data-tip={isTransferring ? `${t('tasks.shared.transferring')}${dots}` : t('tasks.shared.transferCase')}
            aria-label={isTransferring ? `${t('tasks.shared.transferring')}${dots}` : t('tasks.shared.transferCase')}
            disabled={isTransferring || transferId !== null}
            onClick={() => handleTransfer(r)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M7 17 17 7" />
              <path d="M7 7h10v10" />
            </svg>
          </button>
        ) : null;

        const coverNoteBtn = !draft ? (
          <button
            className="act-b"
            type="button"
            data-tip={t('tasks.shared.downloadCoverNote')}
            aria-label={t('tasks.shared.downloadCoverNote')}
            onClick={() => handleOpenCoverNote(r)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        ) : null;

        if (draft && mainTab === 'active' && (subTab === 'mine' || subTab === 'created')) {
          return (
            <div className="act-wrap">
              <button
                className="act-b"
                type="button"
                data-tip={isContinuing ? `${t('tasks.shared.opening')}${dots}` : t('tasks.shared.continueDraft')}
                disabled={isContinuing || continueDraftId !== null}
                onClick={() =>
                  handleContinueDraft({
                    id: rowId,
                    ref: r.ref,
                    fileCategory: r.fileCategory !== EMPTY ? r.fileCategory : undefined,
                  })
                }
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
              <button
                className="act-b"
                type="button"
                data-tip={t('tasks.shared.removeDraft')}
                style={{ color: 'var(--red)' }}
                onClick={() => setConfirmDelete(r)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            </div>
          );
        }

        const viewBtn = (
          <button
            className="act-b"
            type="button"
            data-tip={t('tasks.shared.viewTaskCaseDetails')}
            aria-label={t('tasks.shared.viewTaskCaseDetails')}
            onClick={() => {
              const link = taskCaseLink({
                caseRequestId: r.caseRequestId ?? r.id,
                userTaskId: r.userTaskId,
                viewMode: true,
              });
              nav(link.to, { state: link.state });
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        );

        const editBtn = (
          <Link
            {...taskCaseLink({
              caseRequestId: r.caseRequestId ?? r.id,
              userTaskId: r.userTaskId,
              viewMode: !canUpdate,
            })}
            className="act-b"
            data-tip={
              canUpdate ? t('tasks.shared.updateTaskCaseDetails') : t('tasks.shared.viewTaskCaseDetails')
            }
            aria-label={
              canUpdate ? t('tasks.shared.updateTaskCaseDetails') : t('tasks.shared.viewTaskCaseDetails')
            }
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </Link>
        );

        const retrieveBtn = !isRejected(r) ? (
          <button
            className="act-b"
            type="button"
            data-tip={t('tasks.shared.retrievePhysicalFile')}
            aria-label={t('tasks.shared.retrievePhysicalFile')}
            onClick={() => setRetrievalRow(r)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              <line x1="12" y1="11" x2="12" y2="17" />
              <polyline points="8 15 12 19 16 15" />
            </svg>
          </button>
        ) : null;

        // const markReadBtn = (
        //   <button
        //     className={`mar-btn ${r.hasNewNotification ? 'unread' : 'read-state'}`}
        //     type="button"
        //     data-tip={
        //       isMarking
        //         ? `${t('tasks.shared.updating')}${dots}`
        //         : r.hasNewNotification
        //           ? t('tasks.shared.markAsRead')
        //           : t('tasks.shared.markAsUnread')
        //     }
        //     disabled={isMarking}
        //     onClick={() => handleMarkRead(r)}
        //   >
        //     {r.hasNewNotification ? (
        //       <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        //         <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        //         <polyline points="22 4 12 14.01 9 11.01" />
        //       </svg>
        //     ) : (
        //       <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        //         <circle cx="12" cy="12" r="10" />
        //         <line x1="12" y1="8" x2="12" y2="12" />
        //         <line x1="12" y1="16" x2="12.01" y2="16" />
        //       </svg>
        //     )}
        //   </button>
        // );

        if (mainTab === 'active' && subTab === 'mine') {
          return (
            <div className="act-wrap">
              {/* {markReadBtn} */}
              {editBtn}
              {transferBtn}
              {duplicateBtn}
              {coverNoteBtn}
            </div>
          );
        }

        if (r.isReceivedViaForward) {
          return (
            <div className="act-wrap">
              {/* {markReadBtn} */}
              {viewBtn}
              {editBtn}
              {transferBtn}
              {duplicateBtn}
              {coverNoteBtn}
              {retrieveBtn}
            </div>
          );
        }

        if (mainTab === 'active' && subTab === 'linked') {
          return (
            <div className="act-wrap">
              {viewBtn}
              {transferBtn}
              {duplicateBtn}
              {coverNoteBtn}
            </div>
          );
        }

        if (mainTab === 'active' && subTab === 'created') {
          return (
            <div className="act-wrap">
              {viewBtn}
              {transferBtn}
              {duplicateBtn}
              {coverNoteBtn}
            </div>
          );
        }

        return (
          <div className="act-wrap">
            {viewBtn}
            {transferBtn}
            {duplicateBtn}
            {coverNoteBtn}
            {retrieveBtn}
          </div>
        );
      },
    });

    return base;
  }, [mainTab, subTab, duplicateId, transferId, continueDraftId, dots, handleContinueDraft, handleDuplicate, handleTransfer, handleOpenCoverNote, canUpdate, taskCaseLink, nav, t]);

  const activeTotal = tabCounts.activeMine + tabCounts.activeLinked + tabCounts.activeCreated;

  const legend =
    mainTab === 'active' && subTab === 'mine' ? (
      <>
        <div className="legend-dot" />
        <span>{t('tasks.cases.newNotificationLegend')}</span>
      </>
    ) : mainTab === 'active' && subTab === 'linked' ? (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 18px', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <div className="legend-dot legend-dot--fwd" />
          <span>{t('tasks.cases.forwardedTaskLegend')}</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <div className="legend-dot legend-dot--sub" />
          <span>{t('tasks.cases.newSubTaskLegend')}</span>
        </span>
      </div>
    ) : undefined;

  const suggestScope = scope;

  return (
    <>
      <ConfirmDialog
        isOpen={!!confirmDelete}
        title={t('tasks.cases.deleteTitle')}
        message={t('tasks.cases.deleteMessage')}
        onClose={() => {
          if (!deleteSubmitting) setConfirmDelete(null);
        }}
        onConfirm={runDeleteDraft}
        confirmDisabled={deleteSubmitting}
        confirmLabel={deleteSubmitting ? t('common.deleting') : t('common.yes')}
      />

      <PhysicalFileRetrievalModal
        open={!!retrievalRow}
        submitting={retrievalSubmitting}
        onClose={() => {
          if (!retrievalSubmitting) setRetrievalRow(null);
        }}
        onConfirm={runRetrieval}
      />

      <CriminalCoverNoteModal
        open={!!coverRow && (coverFlow === 'criminal' || coverFlow === 'unknown')}
        caseRequestId={coverCaseRequestId}
        fileRefFallback={coverFileRef}
        onClose={() => setCoverRow(null)}
      />
      <CommonCoverNoteModal
        isOpen={!!coverRow && usesCommonCoverNote(coverFlow)}
        caseRequestId={coverCaseRequestId}
        fileRefFallback={coverFileRef}
        onClose={() => setCoverRow(null)}
      />
      <CriminalAppellateCoverNoteModal
        isOpen={!!coverRow && usesCriminalAppellateCoverNote(coverFlow)}
        caseRequestId={coverCaseRequestId}
        fileRefFallback={coverFileRef}
        onClose={() => setCoverRow(null)}
      />
      <RightToInformationCoverNoteModal
        isOpen={!!coverRow && coverFlow === 'right-to-information'}
        caseRequestId={coverCaseRequestId}
        fileRefFallback={coverFileRef}
        onClose={() => setCoverRow(null)}
      />

      <div className="bc">
        <span>{t('tasks.cases.breadcrumb')}</span>
      </div>

      <div className="page-hdr">
        <div>
          <div className="pg-title">{t('tasks.cases.title')}</div>
        </div>
        <Link to="/tasks/create-case-request" className="btn-create" style={{ textDecoration: 'none' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t('tasks.cases.createCaseRequest')}
        </Link>
      </div>

      <div className="cr-table-wrap">
        <div className="cr-tabs-bar">
          <button
            type="button"
            className={`cr-tab ${mainTab === 'active' ? 'on' : ''}`}
            onClick={() => selectMainTab('active')}
          >
            {t('tasks.cases.activeTab')} <span className="tab-count">{activeTotal}</span>
          </button>
          <button
            type="button"
            className={`cr-tab ${mainTab === 'completed' ? 'on' : ''}`}
            onClick={() => selectMainTab('completed')}
          >
            {t('tasks.cases.completedTab')} <span className="tab-count">{tabCounts.completed}</span>
          </button>
        </div>

        <div className="tab-panel on">
          {mainTab === 'active' ? (
            <div className="subtab-bar">
              <button
                type="button"
                className={`subtab ${subTab === 'mine' ? 'on' : ''}`}
                onClick={() => selectSubTab('mine')}
              >
                {t('tasks.cases.myActiveTab')} <span className="tab-count">{tabCounts.activeMine}</span>
              </button>
              <button
                type="button"
                className={`subtab ${subTab === 'linked' ? 'on' : ''}`}
                onClick={() => selectSubTab('linked')}
              >
                {t('tasks.cases.linkedActiveTab')} <span className="tab-count">{tabCounts.activeLinked}</span>
              </button>
              <button
                type="button"
                className={`subtab ${subTab === 'created' ? 'on' : ''}`}
                onClick={() => selectSubTab('created')}
              >
                {t('tasks.cases.createdCaseRequestsTab')}{' '}
                <span className="tab-count">{tabCounts.activeCreated}</span>
              </button>
            </div>
          ) : null}

          {error ? <div className="si-err" style={{ margin: '12px 18px 0' }}>{error}</div> : null}

          <DataTable
            embedded
            title={t('tasks.cases.tableTitle')}
            legend={legend}
            columns={columns}
            rows={rows}
            rowKey={taskRowKey}
            rowClassName={taskCaseRowHighlightClass}
            defaultRowsPerPage={5}
            rowsPerPageOptions={[5, 10, 15]}
            previousLabel={t('tasks.shared.previousBack')}
            nextLabel={t('tasks.shared.nextForward')}
            loading={loading}
            fetchColumnSuggestions={(col, prefix) => fetchTaskColumnSuggestions(suggestScope, col, prefix)}
            onFiltersChange={handleFiltersChange}
            serverSort={{
              key: sort.key,
              dir: sort.dir,
              onSortChange: handleSortChange,
            }}
            serverPaging={{
              totalCount,
              totalPages,
              page,
              pageSize,
              onPageChange: (p) => setPage(p),
              onPageSizeChange: (n) => {
                setPageSize(n);
                setPage(1);
              },
            }}
          />
        </div>
      </div>
    </>
  );
}
